/**
 * `todayIso` — today's calendar date as `YYYY-MM-DD` in a specific timezone.
 *
 * The codebase historically used `new Date().toISOString().slice(0, 10)` to seed
 * "today" defaults on forms (effective-from dates, occurred-at, allocation
 * dates, …). `toISOString()` returns the **UTC** date, so for a few hours around
 * UTC midnight it renders the wrong day for a user in Jordan (UTC+3) — a Jul 3
 * 01:00 local moment is still Jul 2 in UTC. This helper resolves the date in the
 * intended timezone instead.
 *
 * The timezone is required: there is no house zone at this level. HR's
 * `@linkhr/hr-domain` `todayIso` wraps this with its `Asia/Amman` default.
 *
 * `at` asks the same of another instant (which calendar day it falls on there).
 *
 * Built from `Intl.DateTimeFormat` parts rather than `toLocaleDateString` so the
 * `YYYY-MM-DD` shape is guaranteed regardless of the host's locale-data quirks.
 */
export function todayIso(timeZone: string, at: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(at);
  const part = (type: "year" | "month" | "day"): string =>
    parts.find((p) => p.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}
